import _sodium from 'libsodium-wrappers';

export interface KeyData {
    key: ArrayBuffer,
    iv: ArrayBuffer
}

class DecryptionError extends Error {
    constructor(message: string) {
        super(message); // Call the constructor of the base class `Error`
        this.name = "DecryptionError"; // Set the error name to your custom error class name
        // Set the prototype explicitly to maintain the correct prototype chain
        Object.setPrototypeOf(this, DecryptionError.prototype);
    }
}

export const arrayBufferToBase64 = (buffer : ArrayBuffer) : string => {
  let binary = '';
  const bytes = new window.Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export const base64ToArrayBuffer = async (base64: string): Promise<ArrayBuffer> => {
  return fetch('data:application/octet-stream;base64,' + base64).then((response) => response.arrayBuffer());
};

export const blobAsArrayBuffer = async (blob: Blob): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read Blob as ArrayBuffer'));
      }
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsArrayBuffer(blob);
  });
};

const calculateChecksum = async (fragmentData: ArrayBuffer, algorithm: string): Promise<string> => {
  const hash = await crypto.subtle.digest(algorithm, fragmentData);
  const hashBuffer = new Uint8Array(hash);
  const hashBase64: string = btoa(Uint8Array.from(hashBuffer).reduce((s, b) => s + String.fromCharCode(b), ''));
  return hashBase64;
};

export const calculateUploadChecksum = async (fragmentData: Blob): Promise<string> => {
  return calculateChecksum(await fragmentData.arrayBuffer(), 'SHA-1');
};

export const calculateDownloadChecksum = async (fragmentData: ArrayBuffer): Promise<string> => {
  return calculateChecksum(fragmentData, 'SHA-1');
};

export const calculateHash = async (fragmentData: Blob): Promise<ArrayBuffer> => {
  const hash = await crypto.subtle.digest('SHA-256', await fragmentData.arrayBuffer());
  const hashBuffer = new Uint8Array(hash);
  return hashBuffer;
};

export const decryptSymmetricKeyAndIV = async (key: string, iv: string, cryptoKeyPair: CryptoKeyPair): Promise<KeyData> => {
  const decryptData = async (encryptedData: string, privateKey: CryptoKey, dataType: string): Promise<ArrayBuffer> => {
    const dataBuffer = await base64ToArrayBuffer(encryptedData);
    try {
      return await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, dataBuffer);
    } catch (error) {
      throw new Error(`Failed to decrypt symmetric ${dataType}`);
    }
  };

  const symmetricKey = await decryptData(key, cryptoKeyPair.privateKey, 'key');
  const symmetricIv = await decryptData(iv, cryptoKeyPair.privateKey, 'IV');

  return { key: symmetricKey, iv: symmetricIv };
};

export class EncryptionInfo {
  publicKey: string;
  nonce: string;
  associatedData: string;
  ciphertext: string;
}

export const encrypt = async (
  plaintext: ArrayBuffer,
  otherPartyPublicKey: string,
  chunkIndex: number,
  hash: ArrayBuffer,
): Promise<EncryptionInfo> => {
  const sodium = (await import('libsodium-wrappers')).default;
  await sodium.ready;

  // Generate ephemeral X25519 key pair
  const clientKeyPair = sodium.crypto_kx_keypair();
  const serverPublicKey = sodium.from_base64(otherPartyPublicKey, sodium.base64_variants.ORIGINAL);

  // Derive shared secret (client session key)
  const sharedSecret = sodium.crypto_scalarmult(clientKeyPair.privateKey, serverPublicKey);
  clientKeyPair.privateKey.fill(0); // clear private key

  const info = sodium.from_string('userdata');
  const encryptionKey = await hkdfSha256(sharedSecret, info, 32); //libsodium wrapper does not exist yet for this call

  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);

  // Prepare associated data
  const hashBase64 = sodium.to_base64(hash, sodium.base64_variants.ORIGINAL);
  const nonceBase64 = sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL);
  const publicKeyBase64 = sodium.to_base64(clientKeyPair.publicKey, sodium.base64_variants.ORIGINAL);
  const associatedData = sodium.from_string(
    'plaintext:' +
      hashBase64 +
      ', nonce:' +
      nonceBase64 +
      ', encryptionKey:' +
      publicKeyBase64 +
      ', chunkIndex:' +
      chunkIndex,
  );

  const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    associatedData,
    null,
    nonce,
    encryptionKey,
  );

  return {
    publicKey: publicKeyBase64,
    nonce: nonceBase64,
    associatedData: sodium.to_base64(associatedData, sodium.base64_variants.ORIGINAL),
    ciphertext: arrayBufferToBase64(encrypted),
  };
};

const hkdfSha256 = async (ikm: Uint8Array, info: Uint8Array, length: number): Promise<Uint8Array> => {
  const keyMaterial = await crypto.subtle.importKey('raw', ikm, { name: 'HKDF' }, false, ['deriveKey', 'deriveBits']);

  const derivedKey = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      info: info,
      salt: new Uint8Array(0), // no salt
    },
    keyMaterial,
    length * 8, // length in bits
  );

  return new Uint8Array(derivedKey);
};

export const decryptData = async (encryptedData: ArrayBuffer, key: CryptoKey, iv: ArrayBuffer): Promise<ArrayBuffer> => {
    if (key instanceof CryptoKey) {
        const decryptedData: ArrayBuffer = await self.crypto.subtle.decrypt(
            {
                name: "AES-CBC",
                iv: iv
            },
            key,
            encryptedData).catch(error => {
                throw new DecryptionError("Failed to decrypt data");
            });

        return decryptedData;
    }
    else {
        throw new DecryptionError('Invalid decryption key');
    }
};

export const importKey = async (symmetricKey: ArrayBuffer): Promise<CryptoKey> => {
  try {
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      symmetricKey,
      { name: 'AES-CBC', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    return cryptoKey;
  } catch (error) {
    throw new Error('Error importing key: ' + error.message);
  }
};

export const generateRSAKeyPair = async (): Promise<CryptoKeyPair> => {
  return await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-1',
    },
    true,
    ['encrypt', 'decrypt'],
  );
};

export const exportPublicKey = async (keyPair: CryptoKeyPair): Promise<string> => {
  let publicKey = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);

  const exportedAsString = String.fromCharCode.apply(null, new Uint8Array(publicKey));
  const publicKeyForExport = window.btoa(exportedAsString);
  const pemExported = `-----BEGIN PUBLIC KEY-----\n${publicKeyForExport}\n-----END PUBLIC KEY-----`;

  return pemExported;
};
